import { CSSProperties } from 'react';
import { AssetRef, Resource } from '../../../types';
import { tosThumbnail } from '../../../utils/tosImage';
import { tosAccelerate } from '../../../utils/tosAccelerate';
import { assignTypedTokens } from '../../../utils/assetTokens';
import type { PreviewAsset } from './AssetPreviewLightbox';

type Translate = (key: string, options?: Record<string, unknown>) => string;

// 卡片底部文字型链接按钮（切换状态 / 去生成）共用样式
const cardLinkButtonStyle: CSSProperties = { fontSize: '0.75rem', color: 'var(--theme-storyboard)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', display: 'block', fontWeight: 600 };
const iconButtonStyle: CSSProperties = { position: 'absolute', background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', width: '18px', height: '18px', cursor: 'pointer', color: '#fff', fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 };

interface ShotAssetResourceGridProps {
  refs: AssetRef[];
  resources: Resource[];
  uploadingResource: boolean;
  assetCategoryMap: Map<string, string>;
  projectId: string;
  t: Translate;
  onPreviewAsset: (asset: PreviewAsset) => void;
  onRemoveAssetRef: (imgIndex: number) => void;
  onDeleteResource: (resourceId: string) => void;
  onUploadResource: () => void;
  onPickAsset: () => void;
  onSwitchState: (ref: AssetRef) => void;
}

type GridItem =
  | { kind: 'ref'; ref: AssetRef }
  | { kind: 'res'; resource: Resource; label: string };

const getResourceSourceLabel = (resource: Resource, t: Translate) => {
  if (resource.source === 'preset_audio') return t('shot.presetAudioBadge');
  if (resource.source !== 'shot_ref') return null;
  return resource.type === 'image' ? t('shot.refImage') : t('shot.refVideo');
};

const getRefPreviewType = (ref: AssetRef): PreviewAsset['type'] => (
  ref.source === 'shot_ref' || ref.imageUrl.endsWith('.mp4') ? 'video' : 'image'
);

export default function ShotAssetResourceGrid({
  refs,
  resources,
  uploadingResource,
  assetCategoryMap,
  projectId,
  t,
  onPreviewAsset,
  onRemoveAssetRef,
  onDeleteResource,
  onUploadResource,
  onPickAsset,
  onSwitchState,
}: ShotAssetResourceGridProps) {
  const tokenLabels = assignTypedTokens(refs, resources);
  const refToken = new Map<number, string>();
  for (const it of tokenLabels) {
    if (it.kind === 'ref' && it.imgIndex != null) refToken.set(it.imgIndex, it.token);
  }
  const refByIdx = new Map(refs.map(r => [r.imgIndex, r]));
  const resById = new Map(resources.map(r => [r.id, r]));
  // 渲染顺序 == token 顺序（canonical：refs 按 imgIndex，再 resources 按 时间,id）
  const allItems: GridItem[] = tokenLabels.flatMap(it => {
    if (it.kind === 'ref') {
      const ref = refByIdx.get(it.imgIndex!);
      return ref ? [{ kind: 'ref', ref } as GridItem] : [];
    }
    const resource = resById.get(it.resId!);
    return resource ? [{ kind: 'res', resource, label: it.token } as GridItem] : [];
  });

  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'stretch' }}>
        {allItems.map(item => {
          if (item.kind === 'ref') {
            const ref = item.ref;
            return (
              <div key={`ref-${ref.imgIndex}`} style={{ border: `1px solid ${ref.imageUrl ? 'var(--border)' : 'var(--warning)'}`, borderRadius: 'var(--radius)', overflow: 'visible', background: 'var(--surface)', minWidth: '96px', maxWidth: '144px' }}>
                <div style={{ position: 'relative' }}>
                  {ref.imageUrl ? (
                    getRefPreviewType(ref) === 'video' ? (
                      <video
                        src={tosAccelerate(ref.imageUrl)}
                        preload="metadata"
                        onClick={() => onPreviewAsset({ type: 'video', url: ref.imageUrl, name: ref.name })}
                        style={{ width: '100%', height: '96px', objectFit: 'cover', borderRadius: 'var(--radius) var(--radius) 0 0', display: 'block', cursor: 'zoom-in' }}
                      />
                    ) : (
                      <img src={tosThumbnail(ref.imageUrl, 300)} alt={ref.name} onClick={() => onPreviewAsset({ type: 'image', url: ref.imageUrl, name: ref.name })} style={{ width: '100%', height: '96px', objectFit: 'cover', objectPosition: 'center top', borderRadius: 'var(--radius) var(--radius) 0 0', display: 'block', cursor: 'zoom-in' }} />
                    )
                  ) : (
                    <div style={{ width: '100%', height: '96px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface-2)', borderRadius: 'var(--radius) var(--radius) 0 0', color: 'var(--warning)', fontSize: '1.5rem' }} title={t('shot.missingStateTitle')}>⚠</div>
                  )}
                  <span style={{ position: 'absolute', top: '4px', left: '4px', background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: '0.8rem', borderRadius: '3px', padding: '1px 4px' }}>{refToken.get(ref.imgIndex) || `@${ref.imgIndex}`}</span>
                  {assetCategoryMap.get(ref.type ? `${ref.type}:${ref.name}` : ref.name) && (
                    <span style={{ position: 'absolute', bottom: '4px', left: '4px', background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: '0.7rem', borderRadius: '3px', padding: '1px 4px' }}>{assetCategoryMap.get(ref.type ? `${ref.type}:${ref.name}` : ref.name)}</span>
                  )}
                  <button style={{ ...iconButtonStyle, top: '4px', right: '4px' }} onClick={() => onRemoveAssetRef(ref.imgIndex)}>✕</button>
                </div>
                <div style={{ padding: '4px 6px' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ref.name}</div>
                  {ref.source === 'shot_ref' ? (
                    <div style={{ fontSize: '0.7rem', color: 'var(--primary)', marginTop: '1px' }}>{t('shot.refVideo')}</div>
                  ) : ref.state ? (
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ref.state}</div>
                  ) : null}
                  {ref.allStates && ref.allStates.length >= 1 && (
                    <button onClick={(event) => { event.stopPropagation(); onSwitchState(ref); }} style={cardLinkButtonStyle}>{t('shot.switchState')}</button>
                  )}
                  {!ref.imageUrl && (
                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        window.open(
                          `/projects/${projectId}?openElement=${encodeURIComponent(ref.type || '')}:${encodeURIComponent(ref.name)}&makeState=${encodeURIComponent(ref.state || '')}`,
                          '_blank',
                          'noopener',
                        );
                      }}
                      style={cardLinkButtonStyle}
                    >
                      {t('shot.goGenerate')}
                    </button>
                  )}
                </div>
              </div>
            );
          }

          const { resource, label } = item;
          const shotRefLabel = getResourceSourceLabel(resource, t);
          return (
            <div key={`res-${resource.id}`} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', background: 'var(--surface)', minWidth: '96px', maxWidth: '144px' }}>
              <div style={{ position: 'relative' }}>
                {resource.type === 'image' ? (
                  <img src={tosThumbnail(resource.url, 300)} alt={resource.fileName} onClick={() => onPreviewAsset({ type: 'image', url: resource.url, name: resource.fileName })} style={{ width: '100%', height: '96px', objectFit: 'cover', objectPosition: 'center top', borderRadius: 'var(--radius) var(--radius) 0 0', display: 'block', cursor: 'zoom-in' }} />
                ) : resource.type === 'video' ? (
                  <video
                    src={tosAccelerate(resource.url)}
                    preload="metadata"
                    onClick={() => onPreviewAsset({ type: 'video', url: resource.url, name: resource.fileName })}
                    style={{ width: '100%', height: '96px', objectFit: 'cover', borderRadius: 'var(--radius) var(--radius) 0 0', display: 'block', cursor: 'zoom-in' }}
                  />
                ) : (
                  <div
                    data-testid={`audio-resource-preview-${resource.id}`}
                    onClick={() => onPreviewAsset({ type: 'audio', url: resource.url, name: resource.fileName })}
                    style={{ width: '100%', height: '96px', background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--radius) var(--radius) 0 0', fontSize: '2rem', cursor: 'zoom-in' }}
                  >
                    🔊
                  </div>
                )}
                {label && <span style={{ position: 'absolute', top: '4px', left: '4px', background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: '0.8rem', borderRadius: '3px', padding: '1px 4px' }}>{label}</span>}
                <button style={{ ...iconButtonStyle, top: '4px', right: '4px' }} onClick={() => onDeleteResource(resource.id)}>✕</button>
              </div>
              <div style={{ padding: '4px 6px' }}>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{resource.fileName}</div>
                {shotRefLabel && <div style={{ fontSize: '0.7rem', color: 'var(--primary)', marginTop: '1px' }}>{shotRefLabel}</div>}
              </div>
            </div>
          );
        })}
        <div onClick={() => { if (!uploadingResource) onUploadResource(); }} style={{ width: '72px', height: '72px', border: '1px dashed var(--border-hover)', borderRadius: 'var(--radius)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: uploadingResource ? 'not-allowed' : 'pointer', background: 'var(--surface-2)', flexShrink: 0 }}>
          {uploadingResource ? (
            <span className="loading-spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></span>
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--theme-storyboard)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginTop: '2px' }}>{t('shot.uploadResource')}</span>
            </>
          )}
        </div>
        <div onClick={onPickAsset} style={{ width: '72px', height: '72px', border: '1px dashed var(--border-hover)', borderRadius: 'var(--radius)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'var(--surface-2)', flexShrink: 0 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--theme-storyboard)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
          </svg>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginTop: '2px' }}>{t('shot.selectResource')}</span>
        </div>
      </div>
    </div>
  );
}
